"use client";

import * as React from "react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * AuthBackground - the logo-masked flickering-grid backdrop for the full-page
 * auth stage.
 *
 * Ported from the unified app's `src/auth/FlickerGrid.tsx`: two panels (one
 * bleeding off each edge) of softly flickering navy squares, clipped through
 * the AISquare logo mark via a `destination-in` composite so the mark reads as
 * a textured silhouette. Distinct from the existing `FlickeringGrid` (a
 * full-area square field) - this is the logo-clipped diamond the auth route
 * mounts, hence its own component.
 *
 * Sizing: the mark is a consistent viewport-derived size (so it is identical on
 * login / signup / forgot / reset regardless of the card), and its vertical
 * center follows the auth card via a `ResizeObserver` on the `.gs-card` hook
 * `AuthCard` sets - so the diamond locks to the card as it grows (the password
 * panel expanding) instead of clipping. Falls back to viewport-center when no
 * card is found.
 *
 * SSR-safe: every `window` / `document` / canvas access lives inside
 * `useEffect`. Honors `prefers-reduced-motion` via `useReducedMotion()` - when
 * the user prefers reduced motion it paints a single static frame (texture
 * stays, the strobe stops), exactly like `FlickeringGrid`.
 *
 * `aria-hidden` - purely decorative.
 *
 *   <AuthBackground />                      // default mark + navy squares
 *   <AuthBackground logoSrc="/logo-mark.png" color="hsl(var(--primary))" />
 */
export interface AuthBackgroundProps extends React.HTMLAttributes<HTMLDivElement> {
  /** URL of the logo mark used as the clip mask. Default `/logo-mark.png`. */
  logoSrc?: string;
  /**
   * Square colour. Any CSS colour; a `var(--token)` reference resolves against
   * the stage at mount. Default `hsl(var(--primary))` (the brand navy).
   */
  color?: string;
  /** Peak square opacity (0 to 1). Default 0.5 (matches the unified grid). */
  maxOpacity?: number;
}

/** Resolve one `var(--token)` reference in a colour string against an element. */
const resolveCssColor = (raw: string, el: HTMLElement): string => {
  const varMatch = raw.match(/var\((--[\w-]+)\)/);
  if (!varMatch) return raw;
  const value = getComputedStyle(el).getPropertyValue(varMatch[1]!).trim();
  if (!value) return "hsl(0 0% 64%)";
  // The token holds an HSL triplet (e.g. "213 80% 13%"); wrap it so the canvas
  // can parse it. A bare colour string passes through unchanged.
  return raw.replace(varMatch[0], value);
};

/**
 * Build + animate one masked panel. Returns a teardown. The square size (4px),
 * gap (5px), opacity-lerp (0.06), and re-roll chance (0.05) match the unified
 * `FlickerGrid` exactly so the texture is unchanged.
 */
function runPanel(
  container: HTMLDivElement,
  side: "left" | "right",
  logoImg: HTMLImageElement,
  logoSize: number,
  centerY: number,
  rgbaPrefix: string,
  maxOpacity: number,
  animate: boolean,
): () => void {
  const panelW = Math.ceil(logoSize / 2);
  const panelH = logoSize;
  const offset = Math.round(logoSize * 0.12);

  container.style.cssText =
    "position:absolute;width:" +
    panelW +
    "px;height:" +
    panelH +
    "px;top:" +
    centerY +
    "px;transform:translateY(-50%);pointer-events:none;z-index:0;overflow:hidden;opacity:.6;";
  if (side === "left") container.style.left = -offset + "px";
  else container.style.right = -offset + "px";

  const dpr = window.devicePixelRatio || 1;
  const canvas = document.createElement("canvas");
  canvas.width = panelW * dpr;
  canvas.height = panelH * dpr;
  canvas.style.cssText =
    "position:absolute;top:0;left:0;width:" + panelW + "px;height:" + panelH + "px;";
  container.appendChild(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => canvas.remove();
  ctx.scale(dpr, dpr);

  const maskC = document.createElement("canvas");
  maskC.width = panelW * dpr;
  maskC.height = panelH * dpr;
  const mCtx = maskC.getContext("2d");
  if (!mCtx) return () => canvas.remove();
  mCtx.scale(dpr, dpr);
  if (side === "left") mCtx.drawImage(logoImg, -(logoSize * 0.6), 0, logoSize, logoSize);
  else mCtx.drawImage(logoImg, logoSize * 0.1, 0, logoSize, logoSize);

  const sq = 4;
  const gap = 5;
  const step = sq + gap;
  const cols = Math.ceil(panelW / step) + 1;
  const rows = Math.ceil(panelH / step) + 1;
  const total = cols * rows;
  const ops = new Float32Array(total);
  const tgts = new Float32Array(total);
  for (let i = 0; i < total; i++) {
    ops[i] = Math.random() * maxOpacity;
    tgts[i] = Math.random() * maxOpacity;
  }

  const paint = (advance: boolean) => {
    ctx.clearRect(0, 0, panelW, panelH);
    ctx.globalCompositeOperation = "source-over";
    for (let i = 0; i < total; i++) {
      if (advance) {
        if (Math.random() < 0.05) tgts[i] = Math.random() * maxOpacity;
        ops[i] += (tgts[i] - ops[i]) * 0.06;
      }
      ctx.fillStyle = rgbaPrefix + ops[i]!.toFixed(3) + ")";
      ctx.fillRect((i % cols) * step, Math.floor(i / cols) * step, sq, sq);
    }
    ctx.globalCompositeOperation = "destination-in";
    ctx.drawImage(maskC, 0, 0, panelW, panelH);
    ctx.globalCompositeOperation = "source-over";
  };

  // Static first frame - all reduced-motion users ever see.
  paint(false);

  let raf = 0;
  if (animate) {
    const loop = () => {
      paint(true);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
  }

  return () => {
    cancelAnimationFrame(raf);
    canvas.remove();
  };
}

export const AuthBackground = React.forwardRef<HTMLDivElement, AuthBackgroundProps>(
  (
    { className, logoSrc = "/logo-mark.png", color = "hsl(var(--primary))", maxOpacity = 0.5, ...props },
    ref,
  ) => {
    const leftRef = React.useRef<HTMLDivElement>(null);
    const rightRef = React.useRef<HTMLDivElement>(null);
    const reduced = useReducedMotion();

    React.useEffect(() => {
      const left = leftRef.current;
      const right = rightRef.current;
      if (!left || !right) return;

      // Resolve the (possibly token-driven) colour into an `rgba(r, g, b,`
      // prefix the draw loop appends per-square opacity to.
      const probe = document.createElement("canvas");
      probe.width = probe.height = 1;
      const probeCtx = probe.getContext("2d");
      let rgbaPrefix = "rgba(10, 37, 64,";
      if (probeCtx) {
        probeCtx.fillStyle = resolveCssColor(color, left);
        probeCtx.fillRect(0, 0, 1, 1);
        const data = probeCtx.getImageData(0, 0, 1, 1).data;
        rgbaPrefix = `rgba(${data[0]}, ${data[1]}, ${data[2]},`;
      }

      // The panels are positioned against this component's root (it fills the
      // stage, `absolute inset-0`). The card lives elsewhere in the React tree,
      // so find the stage (the nearest `[data-auth-stage]` ancestor AuthShell
      // marks) and query the `.gs-card` hook AuthCard sets within it. `root` is
      // the panels' offset parent, used as the measurement frame for centering.
      const root = left.parentElement;
      const stage = root?.closest<HTMLElement>("[data-auth-stage]") ?? root ?? null;
      const card = stage?.querySelector<HTMLElement>(".gs-card") ?? null;

      let cleanups: Array<() => void> = [];
      let img: HTMLImageElement | null = null;
      let imgReady = false;

      const tearDown = () => {
        cleanups.forEach((fn) => fn());
        cleanups = [];
      };

      // `null` (pre-resolve) is treated as "motion allowed".
      const animate = reduced !== true;

      const build = () => {
        if (!imgReady || !img) return;
        tearDown();
        // Panels are positioned against `root`, so centre against `root`'s box.
        const frameH = root?.offsetHeight || window.innerHeight;
        const cardRect = card?.getBoundingClientRect();
        const frameRect = root?.getBoundingClientRect();
        const logoSize = Math.max(window.innerHeight * 0.75, 500);
        const centerY =
          cardRect && frameRect
            ? cardRect.top - frameRect.top + cardRect.height / 2
            : frameH / 2;
        cleanups.push(runPanel(left, "left", img, logoSize, centerY, rgbaPrefix, maxOpacity, animate));
        cleanups.push(runPanel(right, "right", img, logoSize, centerY, rgbaPrefix, maxOpacity, animate));
      };

      img = new Image();
      img.onload = () => {
        imgReady = true;
        build();
      };
      img.src = logoSrc;

      // Rebuild when the card resizes (password panel expanding) or the window does.
      const ro =
        card && typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => build()) : null;
      if (ro && card) ro.observe(card);
      const onResize = () => build();
      window.addEventListener("resize", onResize);

      return () => {
        tearDown();
        ro?.disconnect();
        window.removeEventListener("resize", onResize);
      };
    }, [logoSrc, color, maxOpacity, reduced]);

    return (
      <div
        ref={ref}
        aria-hidden
        data-slot="auth-background"
        className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}
        {...props}
      >
        <div ref={leftRef} data-slot="auth-background-left" />
        <div ref={rightRef} data-slot="auth-background-right" />
      </div>
    );
  },
);
AuthBackground.displayName = "AuthBackground";
